import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import AssignmentList from '../AssignmentList';
import type { DashboardTabCounts } from '../AssignmentList';
import { Teacher, YoklaAssignment, ScannedData } from '../../types';
import { logout } from '../../services/authService';
import { displayFenn } from '../../utils/fennUtils';

interface TeacherDashboardProps {
  teacher: Teacher;
}

const Container = styled.div<{ $isDashboard?: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
    ${props => props.$isDashboard && 'padding-bottom: 88px;' /* space for fixed bottom nav */}
  }
`;

const UnifiedHeader = styled.div`
  background: white;
  border-radius: 16px;
  padding: 28px 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;

  // Blue accent bar at top
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 20px 24px;
    gap: 16px;
  }
`;

const HeaderMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

const TitleActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #1e293b;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const MainContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const TeacherInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding-top: 8px;

  @media (max-width: 768px) {
    gap: 12px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const LogoutButton = styled.button`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 13px;
    width: 100%;
    justify-content: center;
  }
`;

const LogoutIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginRight: '6px' }}
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

// Reusable Badge Components
const SubjectBadge = styled.span<{ $isFirst?: boolean }>`
  display: inline-block;
  background: #eff6ff;
  color: #1e40af;
  padding: 4px 12px;
  border-radius: 6px;
  margin-left: ${props => props.$isFirst ? '0' : '8px'};
  font-weight: 600;
  font-size: 13px;
  transition: all 0.2s ease;

  &:hover {
    background: #dbeafe;
    transform: translateY(-1px);
  }
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-weight: 600;
  font-size: 13px;
`;

// Bottom nav: only on dashboard, only on mobile
const BottomNavWrapper = styled.nav<{ $visible: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: ${props => props.$visible ? 'flex' : 'none'};
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background: white;
    border-top: 1px solid #e2e8f0;
    padding: 8px 0;
    padding-bottom: max(8px, env(safe-area-inset-bottom));
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);
    justify-content: space-around;
    align-items: stretch;
  }
`;

const BottomNavItem = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 8px;
  border: none;
  background: transparent;
  position: relative;
  color: ${props => props.$active ? '#1d4ed8' : '#64748b'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease, box-shadow 0.2s ease;

  /* Seçili tabda yuxarı xətt (reng) */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 20%;
    right: 20%;
    height: 3px;
    border-radius: 0 0 3px 3px;
    background: ${props => props.$active ? '#2563eb' : 'transparent'};
    transition: background 0.2s ease;
  }

  &:hover {
    background: transparent;
    color: ${props => props.$active ? '#1d4ed8' : '#475569'};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const BottomNavIconWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const BottomNavBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -10px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  border: 2px solid white;
`;

// Icons for bottom nav (compact)
const NavYoxlamaIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);
const NavApellyasiyaIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);
const NavArxivIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="4" rx="1"/>
    <path d="M4 8h16v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8z"/>
    <path d="M9 12h6"/>
  </svg>
);

const TeacherDashboard: React.FC<TeacherDashboardProps> = ({ teacher }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboardRoute = location.pathname === '/dashboard';

  const [dashboardActiveTab, setDashboardActiveTab] = useState<'assignments' | 'appeals' | 'archive'>('assignments');
  const [tabCounts, setTabCounts] = useState<DashboardTabCounts>({ assignments: 0, appeals: 0, archive: 0 });

  const handleCountsChange = useCallback((counts: DashboardTabCounts) => {
    setTabCounts(counts);
  }, []);

  // Normalize status for display
  const normalizeStatus = (status: string): string => {
    const statusMap: { [key: string]: string } = {
      'teacher': 'Müəllim'
    };
    return statusMap[status] || status;
  };

  const handleAssignmentSelect = async (assignment: YoklaAssignment, students: ScannedData[]) => {
    console.log('🎯 TeacherDashboard: Atama seçildi:', assignment);
    console.log('👥 TeacherDashboard: Öğrenci sayısı:', students.length);

    // Route based on exam type and question number
    if (assignment.examType === 'buraxilis') {
      // Include question number in URL for buraxılış (sulları böl: use first assigned question)
      const questionNum = assignment.questionNumber || assignment.questionNumbers?.[0];
      if (!questionNum) return;
      navigate(`/dashboard/buraxilis-yoxlama/${assignment.id}/${questionNum}`, {
        state: { assignment, students }
      });
    } else if (assignment.examType === 'Blok' && assignment.questionNumber) {
      // Blok exam with questionNumber - new per-question format
      navigate(`/dashboard/yoxlama/${assignment.id}/${assignment.questionNumber}`, {
        state: { assignment, students }
      });
    } else {
      // Blok exam without questionNumber - legacy format (redirects to first question)
      navigate(`/dashboard/yoxlama/${assignment.id}`, {
        state: { assignment, students }
      });
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout hatası:', error);
    }
  };

  return (
    <Container $isDashboard={isDashboardRoute}>
      <UnifiedHeader>
        <HeaderMain>
          <TitleRow>
            <Title>İmtahan Qiymətləndirmə Sistemi</Title>
            <TitleActions>
              <LogoutButton onClick={handleLogout}>
                <LogoutIcon />
                Çıxış
              </LogoutButton>
            </TitleActions>
          </TitleRow>
          <Subtitle>Xoş gəlmisiniz, {teacher.name}</Subtitle>
          <TeacherInfoRow>
            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
              Fənn:
            </span>
            {teacher.fenn.split(',').map((f: string, idx: number) => (
              <SubjectBadge key={idx} $isFirst={idx === 0}>
                {displayFenn(f.trim())}
              </SubjectBadge>
            ))}
            <RoleBadge>Vəzifə: {normalizeStatus(teacher.status)}</RoleBadge>
          </TeacherInfoRow>
        </HeaderMain>
      </UnifiedHeader>
      <MainContent>
        <AssignmentList
          teacherId={teacher.id}
          onAssignmentSelect={handleAssignmentSelect}
          activeTab={isDashboardRoute ? dashboardActiveTab : undefined}
          onTabChange={isDashboardRoute ? setDashboardActiveTab : undefined}
          onCountsChange={isDashboardRoute ? handleCountsChange : undefined}
          hideTabBarOnMobile={isDashboardRoute}
        />
      </MainContent>

      {/* Bottom nav: only on dashboard, only on mobile */}
      <BottomNavWrapper $visible={isDashboardRoute}>
        <BottomNavItem
          $active={dashboardActiveTab === 'assignments'}
          onClick={() => setDashboardActiveTab('assignments')}
        >
          <BottomNavIconWrap>
            <NavYoxlamaIcon />
            {tabCounts.assignments > 0 && (
              <BottomNavBadge>{tabCounts.assignments > 99 ? '99+' : tabCounts.assignments}</BottomNavBadge>
            )}
          </BottomNavIconWrap>
          <span>Yoxlamalar</span>
        </BottomNavItem>
        <BottomNavItem
          $active={dashboardActiveTab === 'appeals'}
          onClick={() => setDashboardActiveTab('appeals')}
        >
          <BottomNavIconWrap>
            <NavApellyasiyaIcon />
            {tabCounts.appeals > 0 && (
              <BottomNavBadge>{tabCounts.appeals > 99 ? '99+' : tabCounts.appeals}</BottomNavBadge>
            )}
          </BottomNavIconWrap>
          <span>Apellyasiyalar</span>
        </BottomNavItem>
        <BottomNavItem
          $active={dashboardActiveTab === 'archive'}
          onClick={() => setDashboardActiveTab('archive')}
        >
          <BottomNavIconWrap>
            <NavArxivIcon />
            {tabCounts.archive > 0 && (
              <BottomNavBadge>{tabCounts.archive > 99 ? '99+' : tabCounts.archive}</BottomNavBadge>
            )}
          </BottomNavIconWrap>
          <span>Arxiv</span>
        </BottomNavItem>
      </BottomNavWrapper>
    </Container>
  );
};

export default TeacherDashboard;



